import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { reads } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import EnvironmentUtils from 'mewe/utils/environment-utils';
import { Theme } from 'mewe/constants';
import Storage from 'mewe/shared/storage';
import { isEmpty } from '@ember/utils';

export default class MwAvatar extends Component {
  @service account;
  @service dynamicPopups;
  @service account;

  @reads('args.group') group;

  @reads('args.page') page;

  @reads('args.owner') owner;

  @reads('args.event') event;

  @reads('args.thread') thread;

  imageSize = this.args.imageSize || '150x150';

  get size() {
    return this.args.size ?? 40;
  }

  get isMe() {
    return this.owner?.id === this.account.activeUser.id;
  }

  get showAmbassadorBadge() {
    if (this.args.isCommunityAvatar) return false;
    if (this.args.hiddenBadges) return false;
    if (this.secondaryAvatarUrl) return false;

    return this.owner?.badges?.ambassador;
  }

  /** @computed */
  @computed('event.id', 'group.id', 'owner.{id,profileId}')
  get profileId() {
    if (!this.owner) {
      return;
    }

    // there might be @group or @event, but we don't want to open sub profile, return profile public link
    // check for explicit false, because we pass it only when we want to prevent opening sub profile
    if (this.args.openSubProfile === false) {
      return this.owner.publicLinkId;
    }

    return (this.group?.id && this.group?.id !== Theme.CONTACTS) && isEmpty(this.event?.id)
      ? this.owner.id
      : this.owner.profileId || this.owner.publicLinkId;
  }

  @computed(
    'args.feedTheme',
    'account.activeUser.id',
    'args.owner._links.avatar.nsfw',
    'args.owner.id',
    'args.isCommunityAvatar',
    'page.isNsfw'
  )
  get isNsfwBlurred() {
    // force blur without further logic, added for testing in UI Book
    if (this.args.isNsfw) {
      return true;
    }

    if (this.args.feedTheme === Theme.CONTACTS) {
      return false;
    }

    if (this.args.page) {
      return this.args.page?.isNsfw;
    }

    if (this.args.owner?.following) {
      return false;
    }
    const isNsfwAllowed = Storage.get(Storage.keys.allowNsfwContentSetting);
    if (this.isMe || isNsfwAllowed) {
      return false;
    }

    return this.args.owner?._links?.avatar?.nsfw;
  }

  /*
    The thing about avatars - we have one store to keep user instances.
    users can have different avatars in different groups - but in store we keep
    instance only with one avatar url, default.

    Api endpoint for groups returns user objects with acting authors,
    these have correct avatar url, but we cannot replace them in store
    ( we would need to keep all avatars for all groups)

    Solution is to ignore url in model instance, and construct correct url there.
   */

  @computed(
    'account.activeUser.fprint',
    'group.id',
    'owner._links.avatar.href',
    'owner.{fingerprint,fprint,id}',
    'page.avatar',
    'args.isCommunityAvatar',
    'imageSize',
    'account.activeUser.isAuthenticated'
  )
  get avatar() {
    let avatar;

    if (this.args.avatarUrl) {
      avatar = this.args.avatarUrl;
    } else if (this.args.isCommunityAvatar) {
      avatar = this.page?.avatar || this.args.event?.avatarLink || this.args.group?.avatarLink;
    } else {
      avatar = this.owner?._links?.avatar?.href;
    }

    if (!avatar) {
      return;
    }

    // add host
    const isUserLoggedIn = Boolean(this.account.isAuthenticated);
    const cdnHost = EnvironmentUtils.getCdnHost();
    const imgHost = EnvironmentUtils.getImgHost(true, !isUserLoggedIn);
    const isBlobData = avatar.includes('data:image');

    // cdnHost might be already included in the url, e.g. group/page/event models add it to predefined cover photos.
    // imgHost might be already included in the url passed from parent.
    // data:image comes from blob data, e.g. when uploading a photo and previewing it before saving.
    // img-proxy - not sure if still needed after checking imgHost and cdnHost above, that's probably for public views.
    if (
      !avatar.includes(cdnHost) &&
      !avatar.includes(imgHost) &&
      !avatar.includes('img-proxy') &&
      !isBlobData
    ) {
      avatar = imgHost + avatar;
    }

    avatar = avatar.replace('{imageSize}', this.imageSize);

    let staticPart = ~avatar.indexOf('?') ? avatar.substr(0, avatar.indexOf('?')) : avatar;
    let params = [];

    if (!this.args.isCommunityAvatar && !isBlobData) {
      if (this.group && this.group.id !== Theme.CONTACTS) {
        params.push(`group=${this.group.id}`);
      }

      if (this.owner?.id === this.account.activeUser?.fprint) {
        params.push(`f=${this.account.activeUser?.fprint}`);
      } else {
        params.push(`f=${this.owner?.fprint || this.owner?.fingerprint || ''}`);
      }
    }

    if (params?.length) {
      return `${staticPart}?${params.join('&')}`;
    }

    return staticPart;
  }

  get mainAvatarUrl() {
    if (this.args.showUserAndCommunity) {
      return this.args.event?.avatarLink || this.args.group?.avatarLink;
    }
    return this.avatar;
  }

  get secondaryAvatarUrl() {
    if (this.args.showUserAndCommunity) {
      return this.avatar;
    }
    return this.args.secondaryAvatarUrl;
  }

  get isAvatarLinkDisabled() {
    return this.args.isCommunityAvatar || this.args.isAuthorLinkDisabled;
  }

  /** @actions */
  @action
  showProfilePopup(element) {
    if (this.args.isAuthorLinkDisabled) {
      return;
    }
    if (this.isMe) {
      return;
    }
    if (this.args.isCommunityAvatar) {
      return;
    }
    if (this.page && !this.owner?.id) {
      return;
    }
    if (!this.account?.activeUser.id) {
      return;
    }
    // TODO: pages need profile popups

    if (!this.isDestroyed && !this.isDestroying && !this.args?.post?.isPublicContent) {
      this.dynamicPopups.openPopup('mw-profile-popup', {
        parent: element,
        avatar: this.avatar,
        owner: this.owner,
        group: this.thread ? this.thread.group : this.group,
        event: this.thread ? this.thread.event : this.event,
        thread: this.thread,
      });
    }
  }
}
